import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const BusinessDashboard = () => {
  const [activeTab, setActiveTab] = useState('invites');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitedFriends, setInvitedFriends] = useState([]);
  const [discountCodes, setDiscountCodes] = useState({ available_codes: [], used_codes: [] });
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // 🔒 PRODUCTION ONLY - No localhost fallback
  const backendUrl = BACKEND_URL;

  useEffect(() => {
    fetchInvitedFriends();
    fetchDiscountCodes();
    fetchSubscriptionInfo();
    fetchAvailablePlans();
  }, []);

  const fetchInvitedFriends = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/business/invites/my-invites`);
      const data = await response.json();
      setInvitedFriends(data.invited_friends || []);
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  };

  const fetchDiscountCodes = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/business/referrals/my-codes`);
      const data = await response.json();
      setDiscountCodes(data);
    } catch (error) {
      console.error('Error fetching discount codes:', error);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/business/subscription/info`);
      const data = await response.json();
      setSubscriptionInfo(data);
    } catch (error) {
      console.error('Error fetching subscription info:', error);
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/business/subscription/available-plans`);
      const data = await response.json();
      setAvailablePlans(data.available_plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${backendUrl}/api/business/invites/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_email: inviteEmail })
      });

      const data = await response.json();

      if (data.success) {
        setMessage('✅ Invite sent successfully!');
        setInviteEmail('');
        fetchInvitedFriends();
      } else {
        setMessage('❌ Failed to send invite');
      }
    } catch (error) {
      setMessage('❌ Error sending invite');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const response = await fetch(`${backendUrl}/api/business/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: planId,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{ color: 'white', fontSize: '2rem', fontWeight: '700' }}>
            💼 Business Dashboard
          </h1>
          <Link to="/" style={{
            padding: '0.75rem 1.5rem',
            background: 'white',
            color: '#667eea',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600'
          }}>
            ← Back to Calculator
          </Link>
        </div>

        {/* Subscription Status Banner */}
        {subscriptionInfo && (
          <div style={{
            background: subscriptionInfo.status === 'trial' ? 'rgba(251, 191, 36, 0.9)' : 'rgba(34, 197, 94, 0.9)',
            padding: '1rem',
            borderRadius: '10px',
            marginBottom: '2rem',
            color: 'white'
          }}>
            <strong>{subscriptionInfo.status.toUpperCase()}</strong> - {subscriptionInfo.days_remaining} days remaining
            ({subscriptionInfo.current_tier} tier)
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          {['invites', 'friends'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === tab ? 'white' : 'rgba(255, 255, 255, 0.2)',
                color: activeTab === tab ? '#667eea' : 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem'
              }}
            >
              {tab === 'invites' && '📧 Send Invites'}
              {tab === 'friends' && '👥 My Friends'}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          {/* Send Invites Tab */}
          {activeTab === 'invites' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Send Invite</h2>
              <form onSubmit={handleSendInvite} style={{ maxWidth: '500px' }}>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="friend@example.com"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: '2px solid #e0e0e0',
                    marginBottom: '1rem'
                  }}
                />
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '0.75rem 2rem',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  {loading ? 'Sending...' : 'Send Invite'}
                </button>
              </form>
              {message && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f0f0', borderRadius: '8px' }}>
                  {message}
                </div>
              )}
            </div>
          )}

          {/* My Friends Tab */}
          {activeTab === 'friends' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                Invited Friends ({invitedFriends.length})
              </h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                      <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '1rem', textAlign: 'left' }}>Win Rate</th>
                      <th style={{ padding: '1rem', textAlign: 'left' }}>Trades</th>
                      <th style={{ padding: '1rem', textAlign: 'left' }}>Paid?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitedFriends.map((friend, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '1rem' }}>{friend.email}</td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            background: friend.registered ? '#dcfce7' : '#fee2e2',
                            color: friend.registered ? '#16a34a' : '#dc2626',
                            fontSize: '0.875rem'
                          }}>
                            {friend.registered ? 'Registered' : 'Pending'}
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          {friend.win_rate ? `${friend.win_rate}%` : '-'}
                        </td>
                        <td style={{ padding: '1rem' }}>{friend.total_trades}</td>
                        <td style={{ padding: '1rem' }}>
                          {friend.converted_to_paid ? '✅' : '❌'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Removed - Discount codes are for regular users only */}
          {activeTab === 'codes' && false && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                My Discount Codes
              </h2>
              
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', color: '#16a34a' }}>
                  ✅ Available Codes ({discountCodes.available_codes.length})
                </h3>
                {discountCodes.available_codes.length === 0 ? (
                  <p style={{ color: '#666' }}>No available codes. Invite friends to earn 50% discount codes!</p>
                ) : (
                  <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
                    {discountCodes.available_codes.map((code, index) => (
                      <div key={index} style={{
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #dcfce7 0%, #86efac 100%)',
                        borderRadius: '10px',
                        border: '2px solid #16a34a'
                      }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#16a34a', marginBottom: '0.5rem', letterSpacing: '2px' }}>
                          {code.code}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#15803d' }}>
                          50% OFF - Never Expires
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', color: '#64748b' }}>
                  Used Codes ({discountCodes.used_codes.length})
                </h3>
                {discountCodes.used_codes.map((code, index) => (
                  <div key={index} style={{
                    padding: '0.75rem',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>{code.code}</span>
                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      Used {code.used_at ? new Date(code.used_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Removed - Upgrade is handled through user profile */}
          {activeTab === 'upgrade' && false && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Upgrade Subscription</h2>
              <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                {availablePlans.map((plan) => (
                  <div key={plan.plan_id} style={{
                    padding: '1.5rem',
                    background: plan.tier === 'demo' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(234, 179, 8, 0.1)',
                    border: `2px solid ${plan.tier === 'demo' ? '#3b82f6' : '#eab308'}`,
                    borderRadius: '12px'
                  }}>
                    <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>{plan.name}</h3>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: plan.tier === 'demo' ? '#3b82f6' : '#eab308', marginBottom: '1rem' }}>
                      ${plan.price}
                    </div>
                    <button
                      onClick={() => handleUpgrade(plan.plan_id)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: plan.tier === 'demo' ? '#3b82f6' : '#eab308',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Subscribe Now
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessDashboard;
